import { cloneElement, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Input, NumericTextBox, TextArea, Checkbox } from "@progress/kendo-react-inputs";
import CleanDatePicker from "src/dm/components/CleanDatePicker/CleanDatePicker";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { useDealDetail } from "src/dm/hooks/useDeals";
import { useDealOwnersList, DealOwnerListRow } from "src/dm/hooks/useLookups";
import UserName from "src/dm/components/UserName/UserName";
import {
  useCopyDealWithOverrides,
  DealCopyOverrides,
} from "src/dm/hooks/useDealActions";
import { DealDetail } from "src/dm/interfaces/Deal";
import * as s from "./CopyDeal.css";

// ---------------------------------------------------------------------------
// CopyDeal SPA page (audit 22-screenshot-inventory.md MAJOR — CopyDeal
// destination picker).
//
// Legacy CopyDeal.aspx is a full page that pre-fills with the source deal's
// editable fields, lets the user override any of them (description, dates,
// allowance flags, copyAttachments toggle), and only then POSTs to the
// CopyDeal handler. The previous SPA action panel skipped this step and
// fired POST .../copy with an empty body — the user never got a chance to
// override anything before being redirected to the new deal.
//
// This page restores parity. It mounts at /deals/:dealId/copy, pre-fills
// from the source deal via useDealDetail, and submits a DealCopyOverrides
// body via the existing useCopyDealWithOverrides hook (the backend already
// accepts the full DealCopyRequest body — see DealsController.Copy and
// audit/23-parity-deal-with-reward.md §1).
//
// Override-vs-clone semantics: any field the user leaves untouched is
// re-sent at the source-deal value, so the server clone still ends up
// matching the source even though the override is non-null. That mirrors
// the legacy CopyDeal.aspx behaviour where the form is fully populated at
// load and the same values are POSTed unchanged. The chkCopyAttachments
// checkbox defaults true to match the legacy form (CopyDeal.aspx.cs:147).
// ---------------------------------------------------------------------------

const toIso = (d: Date | null): string | undefined =>
  d
    ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString()
    : undefined;

const fromIso = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Project a DealDetail onto the DealCopyOverrides shape — the server takes
// every field as optional but we re-send the source values so the form
// labels accurately reflect what the new deal will look like. Strings are
// passed through as-is; null source values become `undefined` so the
// server treats them as "leave alone".
const seedFromSource = (src: DealDetail): DealCopyOverrides => ({
  description: src.description ?? undefined,
  bannerCode: src.bannerCode ?? undefined,
  categoryManagerCode: src.categoryManagerCode ?? undefined,
  dealTypeId: src.dealTypeId ?? undefined,
  adStartDate: src.adStartDate ?? undefined,
  adEndDate: src.adEndDate ?? undefined,
  promoStartDate: src.promoStartDate ?? undefined,
  promoEndDate: src.promoEndDate ?? undefined,
  scanbackStartDate: src.scanbackStartDate ?? undefined,
  scanbackEndDate: src.scanbackEndDate ?? undefined,
  reflectStartDate: src.reflectStartDate ?? undefined,
  reflectEndDate: src.reflectEndDate ?? undefined,
  countRecountStartDate: src.countRecountStartDate ?? undefined,
  countRecountEndDate: src.countRecountEndDate ?? undefined,
  firstOrderDate: undefined,
  lastOrderDate: undefined,
  firstShipmentDate: undefined,
  lastShipmentDate: undefined,
  bestHistoryDate: src.bestHistoryDate ?? undefined,
  signageEndDate: src.signageEndDate ?? undefined,
  rewardPromoEndDate: src.rewardPromotionEndDate ?? undefined,
  promoMaxQty: src.promoMaxQty ?? undefined,
  ownerUserId: src.ownerUserId ?? undefined,
  // Allowance flags — the legacy form lets the user toggle each one
  // independently on the copy. Seed with the source value so the
  // checkboxes start in the right state.
  hasOffInvoiceAllowance: src.hasOffInvoiceAllowance,
  hasBillBackAllowance: src.hasBillBackAllowance,
  hasCountRecountAllowance: src.hasCountRecountAllowance,
  hasPassThruAllowance: src.hasPassThruAllowance,
  hasScanbackAllowance: src.hasScanbackAllowance,
  isRewardPromotion: src.isRewardPromotion,
  // chkCopyAttachments — defaults true (legacy CopyDeal.aspx.cs:147).
  copyAttachments: true,
});

const CopyDeal = () => {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const sourceDealId = dealIdParam ? Number(dealIdParam) : NaN;
  const navigate = useNavigate();

  const source = useDealDetail(
    Number.isFinite(sourceDealId) ? sourceDealId : undefined
  );

  const copy = useCopyDealWithOverrides(sourceDealId);

  // Deal-owners list — used by the Owner DropDownList below. Sized so a
  // single fetch is fine (~hundreds of distinct owners); cached 5 min by the
  // hook so the second copy-page mount doesn't re-fetch.
  const dealOwners = useDealOwnersList();

  // Form state — start blank until the source deal resolves, then seed.
  // Splitting this off from the source query result lets the user edit
  // individual fields without us re-clobbering them on a refetch.
  const [form, setForm] = useState<DealCopyOverrides>({ copyAttachments: true });
  const [seeded, setSeeded] = useState(false);

  // Project the owner list onto the row shape Kendo DropDownList wants. Add
  // a sentinel "Unassigned" row so the user can clear the picker. If the
  // source deal's owner isn't in the distinct list (left team), synthesise a
  // stand-in row so the controlled DropDownList still has a valid selection.
  const ownerData = useMemo<DealOwnerListRow[]>(
    () => [
      { userId: "", fullName: "— Unassigned —" },
      ...(dealOwners.data ?? []),
    ],
    [dealOwners.data]
  );
  const selectedOwnerRow = useMemo<DealOwnerListRow>(() => {
    const id = form.ownerUserId ?? "";
    if (!id) return ownerData[0];
    const match = ownerData.find((o) => o.userId === id);
    if (match) return match;
    return { userId: id, fullName: id };
  }, [form.ownerUserId, ownerData]);

  useEffect(() => {
    if (!seeded && source.data) {
      setForm(seedFromSource(source.data));
      setSeeded(true);
    }
  }, [source.data, seeded]);

  const setField = <K extends keyof DealCopyOverrides>(
    key: K,
    value: DealCopyOverrides[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const errorMessage = useMemo(() => {
    const err = copy.error;
    if (!err) return null;
    const anyErr = err as { response?: { data?: unknown }; message?: string };
    const data = anyErr.response?.data;
    if (data && typeof data === "object") return JSON.stringify(data, null, 2);
    return anyErr.message ?? "Unknown error";
  }, [copy.error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Submit whatever the user has in the form. The backend treats every
    // field as optional + defaults from the source deal where unspecified;
    // sending the full form (including unchanged values) ensures the
    // override is unambiguous and matches the legacy "form-shaped POST".
    const result = await copy.mutateAsync(form);
    // The endpoint returns { deal, warnings }. The hook's TS type predates
    // the warnings field, so we narrow at the call site.
    const resp = result as unknown as
      | { deal?: { dealId?: number }; dealId?: number };
    const newId = resp.deal?.dealId ?? resp.dealId;
    if (newId != null) {
      navigate(`/dm/deals/${newId}`);
    }
  };

  if (!Number.isFinite(sourceDealId)) {
    return <p>Invalid deal id.</p>;
  }
  if (source.isLoading) return <p>Loading source deal {sourceDealId}…</p>;
  if (source.isError) {
    const msg =
      source.error instanceof Error ? source.error.message : "unknown";
    return (
      <div className={s.errorBanner}>
        Could not load source deal {sourceDealId}: {msg}
      </div>
    );
  }
  if (!source.data) return <p>Deal {sourceDealId} not found.</p>;

  const src = source.data;

  return (
    <form onSubmit={handleSubmit} className={s.layout}>
      <Link
        to={`/dm/deals/${sourceDealId}`}
        style={{
          display: "inline-block",
          fontSize: "0.85rem",
          color: "#0a3d62",
          textDecoration: "none",
        }}
      >
        ← Deal {sourceDealId}
      </Link>
      <div className={s.headerCard}>
        <h1 style={{ margin: 0, fontSize: "1.25rem" }}>
          Copy Deal {sourceDealId}
        </h1>
        <span className={s.sourceBadge}>
          Source: {src.description ?? "(no description)"}
        </span>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Override any fields below before clicking Create Copy. Fields you
          leave alone will be cloned from the source deal as-is.
        </span>
      </div>

      {errorMessage && <pre className={s.errorBanner}>{errorMessage}</pre>}

      <div className={s.sectionGrid}>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Basics</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Description</span>
            <Input
              maxLength={200}
              value={form.description ?? ""}
              onChange={(e) =>
                setField(
                  "description",
                  String(e.target.value ?? "") || undefined
                )
              }
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Banner</span>
            <Input
              maxLength={20}
              value={form.bannerCode ?? ""}
              onChange={(e) =>
                setField("bannerCode", String(e.target.value ?? "") || undefined)
              }
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Category Manager Code</span>
            <NumericTextBox
              value={form.categoryManagerCode ?? null}
              onChange={(e) =>
                setField("categoryManagerCode", e.value ?? undefined)
              }
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Deal Type ID</span>
            <NumericTextBox
              value={form.dealTypeId ?? null}
              onChange={(e) => setField("dealTypeId", e.value ?? undefined)}
            />
          </label>
          <label className={s.field}>
            {/*
              Owner DropDownList (carry-forward SPA item 2 — parity with
              DealForm). Sourced from /lookups/deal-owners-list. Items render
              via <UserName> so the friendly name shows up in the list while
              the raw userId remains in the input.
            */}
            <span className={s.fieldLabel}>Owner</span>
            <DropDownList
              data={ownerData}
              textField="userId"
              dataItemKey="userId"
              loading={dealOwners.isLoading}
              filterable={true}
              value={selectedOwnerRow}
              itemRender={(li, itemProps) => {
                const row = itemProps.dataItem as DealOwnerListRow;
                // See DealForm.tsx for why this uses cloneElement instead of
                // spreading li.props — Kendo's element type doesn't conform
                // to React's intrinsic LiHTMLAttributes.
                const body = row.userId ? (
                  <UserName userId={row.userId} />
                ) : (
                  <span style={{ color: "#64748b" }}>{row.fullName}</span>
                );
                return cloneElement(li, li.props, body);
              }}
              onChange={(e) => {
                const row = e.target.value as DealOwnerListRow;
                setField("ownerUserId", row.userId || undefined);
              }}
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Ad &amp; Promotion Dates</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Start</span>
            <CleanDatePicker
              value={fromIso(form.adStartDate)}
              onChange={(e) => setField("adStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad End</span>
            <CleanDatePicker
              value={fromIso(form.adEndDate)}
              onChange={(e) => setField("adEndDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Promo Start</span>
            <CleanDatePicker
              value={fromIso(form.promoStartDate)}
              onChange={(e) => setField("promoStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Promo End</span>
            <CleanDatePicker
              value={fromIso(form.promoEndDate)}
              onChange={(e) => setField("promoEndDate", toIso(e.value))}
            />
          </label>
          {form.isRewardPromotion && (
            <label className={s.field}>
              <span className={s.fieldLabel}>Reward Promo End</span>
              <CleanDatePicker
                value={fromIso(form.rewardPromoEndDate)}
                onChange={(e) =>
                  setField("rewardPromoEndDate", toIso(e.value))
                }
              />
            </label>
          )}
          <label className={s.field}>
            <span className={s.fieldLabel}>Best History</span>
            <CleanDatePicker
              value={fromIso(form.bestHistoryDate)}
              onChange={(e) => setField("bestHistoryDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Signage End</span>
            <CleanDatePicker
              value={fromIso(form.signageEndDate)}
              onChange={(e) => setField("signageEndDate", toIso(e.value))}
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Allowance Dates</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Scanback Start</span>
            <CleanDatePicker
              value={fromIso(form.scanbackStartDate)}
              onChange={(e) => setField("scanbackStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Scanback End</span>
            <CleanDatePicker
              value={fromIso(form.scanbackEndDate)}
              onChange={(e) => setField("scanbackEndDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Reflect Start</span>
            <CleanDatePicker
              value={fromIso(form.reflectStartDate)}
              onChange={(e) => setField("reflectStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Reflect End</span>
            <CleanDatePicker
              value={fromIso(form.reflectEndDate)}
              onChange={(e) => setField("reflectEndDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Count/Recount Start</span>
            <CleanDatePicker
              value={fromIso(form.countRecountStartDate)}
              onChange={(e) =>
                setField("countRecountStartDate", toIso(e.value))
              }
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Count/Recount End</span>
            <CleanDatePicker
              value={fromIso(form.countRecountEndDate)}
              onChange={(e) =>
                setField("countRecountEndDate", toIso(e.value))
              }
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Allowance Flags</h2>
          <div className={s.flagsGrid}>
            <Checkbox
              label="Off-Invoice"
              checked={form.hasOffInvoiceAllowance ?? false}
              onChange={(e) =>
                setField("hasOffInvoiceAllowance", !!e.value)
              }
            />
            <Checkbox
              label="Bill-Back"
              checked={form.hasBillBackAllowance ?? false}
              onChange={(e) =>
                setField("hasBillBackAllowance", !!e.value)
              }
            />
            <Checkbox
              label="Count/Recount"
              checked={form.hasCountRecountAllowance ?? false}
              onChange={(e) =>
                setField("hasCountRecountAllowance", !!e.value)
              }
            />
            <Checkbox
              label="Pass-Thru"
              checked={form.hasPassThruAllowance ?? false}
              onChange={(e) =>
                setField("hasPassThruAllowance", !!e.value)
              }
            />
            <Checkbox
              label="Scanback"
              checked={form.hasScanbackAllowance ?? false}
              onChange={(e) =>
                setField("hasScanbackAllowance", !!e.value)
              }
            />
            <Checkbox
              label="Reward Promotion"
              checked={form.isRewardPromotion ?? false}
              onChange={(e) =>
                setField("isRewardPromotion", !!e.value)
              }
            />
          </div>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Quantities</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Promo Max Qty</span>
            <NumericTextBox
              value={form.promoMaxQty ?? null}
              onChange={(e) => setField("promoMaxQty", e.value ?? undefined)}
            />
          </label>
        </section>
      </div>

      <div className={s.sectionGrid}>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Comments — carried from source</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Consumer Message</span>
            <TextArea
              rows={3}
              value={src.consumerMessageComment ?? ""}
              disabled
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Coupon Comment</span>
            <TextArea rows={3} value={src.couponComment ?? ""} disabled />
          </label>
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Copy Options</h2>
          {/*
            chkCopyAttachments (legacy CopyDeal.aspx.cs:147). When checked,
            the server clones DEAL_ATTACHMENT rows along with the deal
            header. Defaults true to match the legacy form.
          */}
          <label className={s.copyAttachmentsRow}>
            <input
              type="checkbox"
              checked={form.copyAttachments ?? true}
              onChange={(e) =>
                setField("copyAttachments", e.target.checked)
              }
            />
            Copy attachments from source deal
          </label>
          <p
            style={{
              fontSize: "0.75rem",
              color: "#64748b",
              marginTop: "0.5rem",
            }}
          >
            Deal items, rewards, AP vendors, fees, and notes are always
            copied. Audit / signature history is NOT carried over — the new
            deal starts with a clean lifecycle.
          </p>
        </section>
      </div>

      <div className={s.actions}>
        <Button
          type="button"
          themeColor="base"
          onClick={() => navigate(`/dm/deals/${sourceDealId}`)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          themeColor="primary"
          disabled={copy.isPending}
        >
          {copy.isPending ? "Copying…" : "Create Copy"}
        </Button>
      </div>
    </form>
  );
};

export default CopyDeal;
